import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { StopTypes } from 'types/Appointment';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

import { LTLShiplifyLookupMetadata } from './getLTLQuickQuotes';

const SHIPLIFY_LOCATION_LOOKUP_PATH =
  '/quote/quickquote/ltl/shiplify-location-lookup';

export type ShiplifyLocationLookupDestination = {
  addressName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

type ShiplifyLocationLookupRequest = {
  stopType: StopTypes;
  name?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export type ShiplifyLocationLookupResponse = LTLShiplifyLookupMetadata & {
  message?: string;
};

export async function getShiplifyLocationLookup(
  stopType: StopTypes,
  destination: ShiplifyLocationLookupDestination
): Promise<Result<ShiplifyLocationLookupResponse, ApiError>> {
  const { addressName, addressLine1, addressLine2, city, state, zip, country } =
    destination;
  const request: ShiplifyLocationLookupRequest = {
    stopType,
    name: addressName?.trim() || undefined,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode: zip,
    country,
  };

  try {
    const resp = await axios.post<ShiplifyLocationLookupResponse>(
      SHIPLIFY_LOCATION_LOOKUP_PATH,
      request
    );

    return ok(resp.data);
  } catch (error) {
    captureException(error, { functionName: 'getShiplifyLocationLookup' });

    if (
      error instanceof Error &&
      error.message === 'Extension context invalidated.'
    ) {
      throw error;
    }

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to check Shiplify location' });
    }

    if (error.response?.status === 401) {
      throw error;
    }

    return err({
      message:
        error.response?.data?.message || 'Failed to check Shiplify location',
      status: error.response?.status,
      level: error.response?.data?.level || error.response?.data?.errorLevel,
    });
  }
}
